import {
  BaseQueryFn,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/dist/query/react';
import { RootState } from '@store/index';
import { baseUrl } from '../utils/url';
import { ISystemDialog } from '@store/slices/user.slice';
import {
  setExpiredSession,
  setIsMaintenance,
  setSystemDialogs,
} from '@store/slices/api.slice';
import { getCurrentModule } from '@hooks/useCurrentModule';

type ErrorResponse = {
  data: {
    error_message: string;
    errors: {
      error: {
        remarks: string | { name: string; message: string; expiredAt: string };
      };
    }[];
    isMaintenanceMode?: number;
    systemDialogs?: ISystemDialog[];
  };
  message?: string;
  status?: number;
  errorCode: string;
};

let hasTriggeredMaintenance = false;

/**
 * Configures and exports a base query function for API requests.
 * This query function is set up with common configurations and header preparations.
 */

export const baseQuery: BaseQueryFn<
  string | { url: string; method?: string; body?: unknown },
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  if (hasTriggeredMaintenance) {
    return {
      error: { status: 'CUSTOM_ERROR', error: 'App is under maintenance' },
    } as any;
  }

  const result = await fetchBaseQuery({
    baseUrl: baseUrl,
    mode: 'cors',
    credentials: 'include',
    prepareHeaders: async (headers) => {
      const data = api.getState() as RootState;

      if (data) {
        if (
          !headers.get('propertyID') &&
          data.properties.selectedProperty.value !== 'All Properties'
        ) {
          headers.set('propertyID', data.properties.selectedProperty.value);
        }

        const customStationID = headers.get('customStationID');

        if (customStationID) {
          headers.set('stationID', customStationID);
        } else if (data?.posStations?.selectedStation?.stationID) {
          headers.set(
            'stationID',
            data?.posStations?.selectedStation?.stationID,
          );
        }

        const currentModule = getCurrentModule();

        const posSubModuleCode =
          data?.posStations?.selectedStation?.subModuleCode;

        const shouldSetPOSSubModule = Boolean(
          (['POS', 'BO'].includes(currentModule) && posSubModuleCode) ||
          data?.posStations?.isInitialSetupActive,
        );

        headers.set(
          'subModule',
          shouldSetPOSSubModule ? (posSubModuleCode ?? '') : '',
        );

        if (
          data.selectedAccountingPeriod.selectedAccountingPeriod
            .accountingPeriodID
        ) {
          headers.set(
            'accountingPeriod',
            data.selectedAccountingPeriod.selectedAccountingPeriod.periodName,
          );
        }
        headers.set('x-access-token', data.user.info.token);
      }
      return headers;
    },
  })(args, api, extraOptions);

  // TOAST
  // enqueueSnackbar('success');

  // MODAL OR DIALOG

  if (result.error?.data) {
    const errorData = result.error.data as ErrorResponse;

    const isMaintenanceError =
      (result.error?.status === 401 || errorData?.status === 401) &&
      ['currently under maintenance', 'under maintenance'].some((keyword) =>
        errorData?.message?.toLowerCase().includes(keyword),
      );

    // 1. Maintenance mode
    if (Boolean(errorData?.data?.isMaintenanceMode) || isMaintenanceError) {
      hasTriggeredMaintenance = true;
      api.dispatch(setIsMaintenance(true));
      api.dispatch(
        setSystemDialogs(errorData?.data?.systemDialogs! || [errorData]),
      );
      return result;
    }

    // 2. Expired session (401/403 with an "expired token" message)
    const status = Number(result.error?.status ?? errorData?.status);
    const messages = [errorData?.message, errorData?.data?.error_message].map(
      (m) => (typeof m === 'string' ? m.toLowerCase() : ''),
    );

    // AUTH002 is shared with MFA errors, so check the backend message to identify expired sessions.
    const isAuthError = status === 401 || status === 403;
    const hasExpiredSessionMessage = messages.some(
      (message) =>
        message.includes('expired token') ||
        message.includes('session not found') ||
        message.includes('invalid or expired token'),
    );

    if (isAuthError && hasExpiredSessionMessage) {
      api.dispatch(setExpiredSession(true));
      return result;
    }
  }

  return result;
};
